import React, {
	useEffect,
	useState,
} from "react";
import "./ProductView.scss";
import {
	Box,
	Grid,
	TextField,
	Button,
	Paper,
	Typography,
	MenuItem,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SellerDependentView from "../Shared/SellerDependentView";
import { GetModalValuesFromBulkResponse } from "../../utils/translateBulkResponse";
import { LoadableContent } from "../Shared/LoadableContent/LoadableContent";
import InventoryTable from "./components/InventoryTable";
import { DataContext } from "../../App";
import Modal from "../Shared/Modals/Modal";
import { RecordsTotal } from "../Shared/ResultsFound/ResultsFound";
import { AlertBox } from "../Shared/Alert/Alert";
import SearchFilter from "../Shared/SearchFilter/SearchFilter";
import * as tableConstants from "../Shared/Config/TableConfig";
import { Link as RouterLink } from "react-router-dom";
import {
	productStatusPending,
	productStatusActive,
	productStatusRejected
} from "../../utils/statusColors";
import { getIdByValue } from "../../utils/object";
import { getCSVFileFromJSONArray } from "../../utils/csv";
import { getDisplayDate } from "../../utils/date";

export default function ProductView({ seller, userdata }) {

	if (!seller || !seller.id || !userdata) return null;

	const context = React.useContext(DataContext);
	const [state, setState] = useState({
		filter: "",
		statusID: 0,
		selectedWarehouse: "",
		filteredInventory: [],
		totalInventory: 0,
		page: 1,
		rowsPerPage: tableConstants.PAGINATION_OPTIONS[0],
		sortColumn: "sku",
		sortDirection: "asc",
		productStatuses: {},
		warehouses: [],
		selectedBulkAction: "",
		selectedProducts: [],
		reloadInventory: false,
		isLoading: false,
		isModalOpen: false,
		modalBodyTitle: "",
		modalBodyMessage1: "",
		modalBodyMessage1Data: [],
		modalBodyMessage1List: "",
		modalBodyMessage2: "",
		modalBodyInputFields: {},
		modalButtonVariant1: "",
		modalButtonText1: "",
		modalButtonActionType1: "",
		modalButtonAction1: "",
		modalButtonColor1: "primary",
		modalButtonVariant2: "",
		modalButtonText2: "",
		modalButtonActionType2: "",
		modalButtonAction2: "",
		modalButtonColor2: "primary",
		isHideSelectionLoading: false,
		hideSelectionError: false,
	});

	const userHasPermissionViewBulkActions = userdata.permissions && userdata.permissions.has("view_bulk_actions");
	const userHasPermissionHideSKUs = userdata.permissions && userdata.permissions.has("bulk_operation_hide_skus");
	const userHasPermissionUpdateProductStatus = userdata.permissions && userdata.permissions.has("update_product_status");
	const userHasCreateProductPermission = userdata.permissions && userdata.permissions.has("create_product");

	useEffect(() => {
		const getProductStatuses = async () => {
			try {
				const response = await context.dataProvider.getProductStatuses();
				setState(previousState => ({
					...previousState,
					productStatuses: response.data,
				}));
			} catch (error) {
				setState(previousState => ({
					...previousState,
					productStatuses: {},
				}));
			}
		};
		getProductStatuses();
	}, []);

	useEffect(() => {
		const getSellerWarehouses = async () => {
			try {
				const warehousesResponse = await context.dataProvider.getSellerWarehouses(seller.id);
				setState(previousState => ({
					...previousState,
					warehouses: warehousesResponse.data,
				}));
			} catch (error) {
				setState(previousState => ({
					...previousState,
					warehouses: [],
				}));
			}
		};
		getSellerWarehouses();
	}, [context.dataProvider, seller.id]);

	useEffect(() => {
		const getInventoryData = async () => {
			setState(previousState => ({
				...previousState,
				isLoading: true,
				filteredInventory: [],
				totalInventory: 0,
			}));

			try {
				const warehouseID = state.selectedWarehouse === "" || state.selectedWarehouse === "all" ? 0 : state.selectedWarehouse;

				const [responseBundles, responseStock] = await Promise.all([
					context.dataProvider.getAllBundles(seller.id),
					context.dataProvider.getStock(seller.id, warehouseID, userHasPermissionHideSKUs, state.statusID, {
						page: state.page,
						results: state.rowsPerPage,
						filter: state.filter,
						sortColumn: state.sortColumn,
						sortDirection: state.sortDirection,
					})
				]);

				const bundlesMap = new Map();
				responseBundles.data.forEach(bundle => bundlesMap.set(bundle.sku, bundle.components));

				const inventory = responseStock.data.stock.map(product => ({
					...product,
					bundle_components: bundlesMap.get(product.sku) || []
				}));

				setState(previousState => ({
					...previousState,
					filteredInventory: inventory,
					totalInventory: responseStock.data.total,
					isLoading: false,
				}));
			} catch (error) {
				setState(previousState => ({
					...previousState,
					filteredInventory: [],
					totalInventory: 0,
					isLoading: false,
				}));
			}
		};

		getInventoryData();

	}, [context.dataProvider, seller.id, state.selectedWarehouse, state.statusID, state.reloadInventory, userHasPermissionHideSKUs, state.page, state.rowsPerPage, state.filter, state.sortColumn, state.sortDirection]);

	const onChangeSelectedProducts = (products) => {
		setState(previousState => {
			return {
				...previousState,
				selectedProducts: products
			};
		});
	};

	const SelectWarehouse = ({
		...otherProps
	}) => {

		const onChangeWarehouseSelect = e => {
			const selected = e.target.value;
			setState(previousState => ({
				...previousState,
				selectedWarehouse: selected,
			}));
		};

		const configSelect = {
			...otherProps,
			select: true,
			variant: "outlined",
			onChange: onChangeWarehouseSelect
		};

		return (
			<TextField
				sx={{ minWidth: 150, marginRight: "2rem", marginTop: "0" }}
				label="Warehouse"
				name="warehouse"
				size="small"
				margin="normal"
				value={state.selectedWarehouse}
				{...configSelect}>
				<MenuItem value="all">
					All
				</MenuItem>
				{state.warehouses.map(({ id, name }) => (
					<MenuItem key={id} value={id}>
						{name}
					</MenuItem>
				))}
			</TextField>
		);
	};

	const onClickDownloadProductsAwaitingVerification = async () => {
		const statusID = getIdByValue(state.productStatuses, "awaitingverification");

		const formattedDate = getDisplayDate(Date.now(), "", "-");
		const filename = seller.code + " Products Awaiting Verification " + formattedDate + ".csv";

		context.dataProvider.getAllProducts(seller.id, statusID)
			.then((res) => {
				if (res.data && res.data.length > 0) {
					getCSVFileFromJSONArray(res.data, filename);
				}
			});
	};

	const collectAllHiddenSkus = async () => {
		const hiddenSkus = new Set();
		let page = 1;
		const results = 500;
		let total = Infinity;

		while ((page - 1) * results < total) {
			const response = await context.dataProvider.getStock(
				seller.id,
				0,
				true,
				0,
				{
					page,
					results,
					filter: "",
					sortColumn: "sku",
					sortDirection: "asc",
				}
			);

			const stock = response.data?.stock ?? [];
			stock
				.filter((product) => product.is_hidden_for_seller === true)
				.forEach((product) => hiddenSkus.add(product.sku));
			total = response.data?.total ?? 0;
			page += 1;
		}

		return [...hiddenSkus];
	};

	const onChangeSelectedBulkAction = (e) => {
		const bulkAction = e.target.value;

		if (bulkAction) {
			let statusID = 0;

			if (bulkAction === "approve-skus") {
				statusID = getIdByValue(state.productStatuses, "awaitingverification");
			}

			setState(previousState => ({
				...previousState,
				selectedBulkAction: bulkAction,
				isLoaded: true,
				statusID,
				selectedProducts: [],
				isHideSelectionLoading: bulkAction === "hide-skus",
				hideSelectionError: false,
			}));

			if (bulkAction === "hide-skus") {
				collectAllHiddenSkus()
					.then((hiddenSkus) => {
						setState((previousState) => {
							if (previousState.selectedBulkAction !== "hide-skus") {
								return previousState;
							}

							return {
								...previousState,
								selectedProducts: [...new Set([...previousState.selectedProducts, ...hiddenSkus])],
								isHideSelectionLoading: false,
								hideSelectionError: false,
							};
						});
					})
					.catch(() => {
						setState((previousState) => {
							if (previousState.selectedBulkAction !== "hide-skus") {
								return previousState;
							}

							const pageHiddenSkus = previousState.filteredInventory
								.filter((product) => product.is_hidden_for_seller === true)
								.map((product) => product.sku);

							return {
								...previousState,
								isHideSelectionLoading: false,
								hideSelectionError: true,
								selectedProducts: [...new Set([...previousState.selectedProducts, ...pageHiddenSkus])],
							};
						});
					});
			}
		}
	};

	const resetBulkActionSelection = () => {
		setState(previousState => ({
			...previousState,
			isLoaded: true,
			selectedProducts: [],
			selectedBulkAction: "",
			isModalOpen: false,
			statusID: 0,
			isHideSelectionLoading: false,
			hideSelectionError: false,
		}));
	};

	const showHideSKUsModal = () => {
		setState(previousState => ({
			...previousState,
			modalBodyTitle: "Bulk Action: Hide SKUs",
			modalBodyMessage1: "Please confirm you would like to hide the selected SKUs.",
			modalBodyMessage2: "",
			modalButtonVariant1: "contained",
			modalButtonText1: "No",
			modalButtonActionType1: "close",
			modalButtonAction1: "",
			modalBodyMessage1Data: [],
			modalButtonVariant2: "contained",
			modalButtonText2: "Yes, hide selected SKUs",
			modalButtonActionType2: "function",
			modalButtonAction2: onClickHideSKUs,
			modalButtonColor2: "destructive",
			isModalOpen: true,
		}));
	};

	const showUpdateProductStatusModal = (action) => {
		let status = productStatusPending;
		let inputFields = {};
		if (action === "approve") {
			status = productStatusActive;
		} else if (action === "reject") {
			status = productStatusRejected;

			inputFields = {
				"rejection_comment": {
					type: "string",
					label: "Comment",
				},
			};
		}

		setState(previousState => ({
			...previousState,
			modalBodyTitle: "Bulk Action: Approve SKUs",
			modalBodyMessage1: `Please confirm you would like to ${action} the selected SKUs.`,
			modalBodyMessage2: "",
			modalButtonVariant1: "contained",
			modalButtonText1: "Cancel",
			modalButtonActionType1: "close",
			modalButtonAction1: "",
			modalButtonColor1: "destructive",
			modalBodyMessage1Data: [],
			modalBodyInputFields: inputFields,
			modalButtonVariant2: "contained",
			modalButtonText2: `Yes, ${action} selected SKUs`,
			modalButtonActionType2: "function",
			modalButtonAction2: (values) => onClickUpdateProductStatus(values, status),
			isModalOpen: true,
		}));
	};

	const onClickHideSKUs = async () => {
		const options = {
			operationName: "hide products",
			successfulOperation: "Hidden",
			success: "Products successfully hidden",
			onClickCloseButton: resetBulkActionSelection,
		};

		let res;
		try {
			res = await context.dataProvider.hideProducts(seller.id, state.selectedProducts);
		} catch (error) {
			res = error.response;
		}

		const newState = GetModalValuesFromBulkResponse(res, options);
		setState(previousState => ({
			...previousState,
			...newState,
			reloadInventory: !state.reloadInventory,
		}));
	};

	const onClickUpdateProductStatus = async (values, status) => {
		const statusID = getIdByValue(state.productStatuses, status);
		const comment = status === productStatusRejected ? (values.rejection_comment || "") : "";

		const productStatuses = {
			status_id: Number(statusID),
			skus: state.selectedProducts,
			comment: comment,
		};

		let res;
		try {
			res = await context.dataProvider.updateProductStatuses(seller.id, productStatuses);
		} catch (error) {
			res = error.response;
		}

		const options = {
			operationName: "update product status",
			successfulOperation: "Statuses Updated",
			success: "Product statuses successfully updated",
			onClickCloseButton: resetBulkActionSelection,
		};

		const newState = GetModalValuesFromBulkResponse(res, options);
		setState(previousState => ({
			...previousState,
			...newState,
			statusID: 0,
		}));
	};

	const onModalClose = (props) => {
		resetBulkActionSelection();

		setState(previousState => ({
			...previousState,
			isModalOpen: props,
		}));
	};

	const handleFilterChange = (value) => {
		setState(previousState => ({
			...previousState,
			filter: value,
			page: 1,
		}));
	};

	const handleSortChange = (column, direction) => {
		setState(previousState => ({
			...previousState,
			sortColumn: column,
			sortDirection: direction,
			page: 1,
		}));
	};

	const handlePageChange = (newPage) => {
		setState(previousState => ({
			...previousState,
			page: newPage,
		}));
	};

	const handleRowsPerPageChange = (newRowsPerPage) => {
		setState(previousState => ({
			...previousState,
			rowsPerPage: newRowsPerPage,
			page: 1,
		}));
	};

	return (
		<Box id="product-view">
			<SellerDependentView seller={seller.id}>
				<Box>
					<Grid container spacing={1}>
						{userHasCreateProductPermission &&
							<>
								<Grid item xs="auto">
									<Button
										variant="contained"
										component={RouterLink}
										to={"/product-create"}
									>
										Create a product<AddIcon />
									</Button>
								</Grid>
								<Grid item xs="auto">
									<Button
										color="secondary"
										variant="outlined"
										component={RouterLink}
										to="/inventory-upload"
									>
										Import Products<AddIcon />
									</Button>
								</Grid>
							</>
						}
						<Grid item xs />
					</Grid>

					<Paper sx={{ p: 2, width: "100%", mt: 3 }} elevation={2}>
						<Typography variant="h5">
							Inventory <RecordsTotal total={state.totalInventory} />
						</Typography>

						<Grid item container className="form-inputs">

							{userHasPermissionViewBulkActions &&
								<Grid item xs="auto">
									<TextField
										select
										fullWidth
										size="small"
										sx={{ minWidth: "200px", width: "auto", paddingRight: "1rem", mb: 2 }}
										id="bulk-actions-select"
										inputProps={{ id: "bulk-actions-select", "data-testid": "bulk-actions-select" }}
										label="Bulk Actions"
										value={state.selectedBulkAction}
										onChange={(e) => onChangeSelectedBulkAction(e)}
									>
										{userHasPermissionHideSKUs &&
											<MenuItem value={"hide-skus"}>
												Hide SKUs
											</MenuItem>
										}
										{userHasPermissionUpdateProductStatus &&
											<MenuItem value={"approve-skus"}>
												Approve SKUs
											</MenuItem>
										}
									</TextField>
								</Grid>
							}

							<Grid item xs />

							{state.warehouses.length > 1 &&
								<Grid item xs="auto" mb={1}>
									<SelectWarehouse />
								</Grid>
							}

							<Grid item xs="auto">
								<SearchFilter
									filter={state.filter}
									onFilterChange={handleFilterChange}
									label={"Search"}
								/>
							</Grid>

						</Grid>

						{userHasPermissionViewBulkActions && state.selectedBulkAction &&
							<Grid
								item container
								className="form-inputs">
								<Grid item xs="auto">
									{state.selectedBulkAction === "hide-skus" &&
										<AlertBox
											severity={state.hideSelectionError ? "error" : "info"}
											message={state.hideSelectionError
												? "Could not load all hidden SKUs. Confirm is disabled to prevent accidental changes. Cancel and try again."
												: "Please select the SKUs you would like to hide."}
										/>
									}
									{state.selectedBulkAction === "approve-skus" &&
										<AlertBox
											severity="info"
											message="Please select the SKUs you would like to approve/reject."
										/>
									}
								</Grid>

								<Grid item xs="auto">
									{state.selectedBulkAction === "hide-skus" &&
										<Button
											variant="contained"
											sx={{ mr: 1, mb: 1 }}
											className='button-confirm'
											onClick={showHideSKUsModal}
											disabled={state.isHideSelectionLoading || state.hideSelectionError}
										>
											Confirm
										</Button>
									}
									{state.selectedBulkAction === "approve-skus" &&
										<>
											<Button
												variant="outlined"
												sx={{ mb: 1, mr: 1 }}
												className='button-confirm'
												onClick={() => onClickDownloadProductsAwaitingVerification()}
											>
												Export Products
											</Button>
											<Button
												variant="contained"
												sx={{ mb: 1, mr: 1 }}
												className='button-confirm'
												onClick={() => showUpdateProductStatusModal("approve")}
											>
												Approve
											</Button>
											<Button
												variant="contained"
												sx={{ mb: 1, mr: 1 }}
												className='button-confirm'
												color="destructive"
												onClick={() => showUpdateProductStatusModal("reject")}
											>
												Reject
											</Button>
										</>
									}
									<Button
										variant="contained"
										sx={{ mb: 1 }}
										color="destructive"
										className='button-cancel'
										onClick={resetBulkActionSelection}
									>
										Cancel
									</Button>
								</Grid>

							</Grid>
						}

						<LoadableContent isLoading={state.isLoading}>
							<InventoryTable
								inventory={state.filteredInventory}
								warehouses={state.warehouses}
								isVisibleBulkAction={state.selectedBulkAction}
								isVisibleIsHiddenForSeller={userHasPermissionHideSKUs}
								onChangeSelectedProducts={onChangeSelectedProducts}
								selectedProducts={state.selectedProducts}
								productStatuses={state.productStatuses}
								page={state.page}
								rowsPerPage={state.rowsPerPage}
								totalCount={state.totalInventory}
								onPageChange={handlePageChange}
								onRowsPerPageChange={handleRowsPerPageChange}
								sortColumn={state.sortColumn}
								sortDirection={state.sortDirection}
								onSortChange={handleSortChange}
							/>
							<Modal
								onModalClose={onModalClose}
								isModalOpen={state.isModalOpen}
								modalBodyTitle={state.modalBodyTitle}
								modalBodyMessage1={state.modalBodyMessage1}
								modalBodyMessage1Data={state.modalBodyMessage1Data}
								modalBodyMessage1List={state.modalBodyMessage1List}
								modalBodyMessage2={state.modalBodyMessage2}
								modalBodyInputFields={state.modalBodyInputFields}
								modalButtonVariant1={state.modalButtonVariant1}
								modalButtonText1={state.modalButtonText1}
								modalButtonActionType1={state.modalButtonActionType1}
								modalButtonAction1={state.modalButtonAction1}
								modalButtonColor1={state.modalButtonColor1}
								modalButtonVariant2={state.modalButtonVariant2}
								modalButtonText2={state.modalButtonText2}
								modalButtonActionType2={state.modalButtonActionType2}
								modalButtonAction2={state.modalButtonAction2}
								modalButtonColor2={state.modalButtonColor2}
							/>
						</LoadableContent>
					</Paper>
				</Box>
			</SellerDependentView>
		</Box>
	);
}
